/**
 * iter-338n: AIChatWidget — holographic / futuristic redesign.
 *
 * - Larger panel: 440×640 with auto-grow textarea (3 rows initial, max 6).
 * - White glass + pastel-gradient theme (purple→blue→cyan), backdrop-blur.
 * - Animated pulse on the launcher orb (CSS keyframes).
 * - Suggested-question chips on the empty state — one tap to try.
 * - Clickable BOQ-row cards with mini-card layout + ExternalLink icon.
 * - Owner/Admin/Staff see ∞ pill (unlimited), other roles see numeric balance.
 *
 * IMPORTANT: data-testids preserved exactly so existing tests keep passing:
 *   ai-chat-launcher · ai-chat-panel · ai-chat-credits · ai-chat-new-session
 *   ai-chat-close · ai-chat-messages · ai-chat-input · ai-chat-send
 *   ai-chat-msg-{role}-{i} · ai-chat-card-{i}-{j}
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, Send, Loader2, Wallet, Infinity as InfinityIcon, ExternalLink, Plus, Download, FileText, FileSpreadsheet, Star, Clock } from 'lucide-react';
import EmailReportCard from './EmailReportCard';
import BOQResultsList from './BOQResultsList';
// iter-364 — Featured Brand Partner placement inside AI Assistant chat widget.
import SponsorBadge from './SponsorBadge';
import { CreditBadge } from '@/components/CreditBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(d) { try { return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } }
function fmtINR(n) { return n != null ? `Rs ${Math.round(+n || 0).toLocaleString('en-IN')}` : '—'; }

// iter-338q: Action-Oriented — fetch the report endpoint with the user's
// bearer token and trigger a blob download. Returns toast feedback on errors.
async function downloadReport(endpoint, filename) {
  const token = localStorage.getItem('token');
  if (!token) { toast.error('Auth token missing — please re-login'); return; }
  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
  const tid = `ai-report-dl-${Date.now()}`;
  toast.loading('Generating report…', { id: tid });
  try {
    const res = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`(${res.status}) ${txt.slice(0, 120)}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'report.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Downloaded', { id: tid });
  } catch (e) {
    toast.error(`Download failed: ${e.message || e}`, { id: tid });
  } finally {
    // Always dismiss the loading toast so it never sticks.
    setTimeout(() => toast.dismiss(tid), 4000);
  }
}

// iter-338u: role-aware quick-prompt bank. The AIChatWidget looks up the
// logged-in user's role in localStorage and shows the tailored menu.
const SUGGESTIONS_BY_ROLE = {
  client: [
    'What is my outstanding balance?',
    'Show my pending approvals',
    'Email me my latest BOQ pdf',
    'What are the upcoming milestones on my project?',
  ],
  vendor: [
    'Which BOQs are assigned to me right now?',
    'Show my pending vendor payments',
    'Where are my quote submissions?',
    'What tasks are due for me this week?',
  ],
  labour: [
    'What tasks are assigned to me?',
    'Show my next site visit',
    'Where do I mark task progress?',
  ],
  default: [
    'Where was polycab wire used previously?',
    'Show kitchen worktops with rates above 5000',
    'Which BOQs contain false ceiling tasks?',
    'Find recent wardrobe items',
  ],
};

function getRoleSuggestions() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const role = (u.role || '').toLowerCase();
    if (SUGGESTIONS_BY_ROLE[role]) return SUGGESTIONS_BY_ROLE[role];
  } catch { /* noop */ }
  return SUGGESTIONS_BY_ROLE.default;
}

const SUGGESTIONS = SUGGESTIONS_BY_ROLE.default;  // fallback for existing refs

export default function AIChatWidget({ scope = null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [credits, setCredits] = useState(null);
  const [unlimited, setUnlimited] = useState(false);
  // iter-338y: saved / recent searches. Loaded when the widget opens.
  const [pinnedSearches, setPinnedSearches] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchCredits = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/ai-chat/credits`, { headers: authHeaders });
      setCredits(r.data?.balance ?? null);
      setUnlimited(!!r.data?.unlimited);
    } catch { /* silent */ }
  }, [token]);

  // iter-338y: fetch pinned + recent saved searches to render on welcome screen
  const fetchSavedSearches = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/ai-chat/saved-searches`, { headers: authHeaders });
      setPinnedSearches(r.data?.pinned || []);
      setRecentSearches(r.data?.recent || []);
    } catch { /* silent */ }
  }, [token]);

  const togglePinSearch = async (query, isPinned) => {
    const endpoint = isPinned ? 'unpin' : 'pin';
    try {
      await axios.post(`${API}/ai-chat/saved-searches/${endpoint}`, { query }, { headers: authHeaders });
      toast.success(isPinned ? 'Unpinned' : 'Pinned');
      fetchSavedSearches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteSavedSearch = async (id) => {
    try {
      await axios.delete(`${API}/ai-chat/saved-searches/${id}`, { headers: authHeaders });
      fetchSavedSearches();
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (open) {
      fetchCredits();
      fetchSavedSearches();
    }
  }, [open, fetchCredits, fetchSavedSearches]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);
  useEffect(() => { if (open && inputRef.current) setTimeout(() => inputRef.current.focus(), 50); }, [open]);

  const sendWith = async (text) => {
    if (!text || sending) return;
    setSending(true);
    const userMsg = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    try {
      const r = await axios.post(`${API}/ai-chat/message`, { message: text, session_id: sessionId, scope }, { headers: authHeaders });
      setSessionId(r.data.session_id);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: r.data.reply,
        tool_used: r.data.tool_used,
        tool_results: r.data.tool_results || [],
        tool_calls: r.data.tool_calls || [],
        semantic_terms: r.data.semantic_terms || [],
        created_at: new Date().toISOString(),
      }]);
      if (r.data.unlimited) { setUnlimited(true); setCredits(null); }
      else if (r.data.credits_remaining != null) { setCredits(r.data.credits_remaining); setUnlimited(false); }
      // iter-338y polish: refresh recent-searches list in-session so a new
      // query lands in the "Recent" chip row without needing to close/reopen.
      fetchSavedSearches();
    } catch (e) {
      const msg = e.response?.data?.detail || 'AI request failed';
      setMessages((prev) => [...prev, { role: 'system', content: msg, created_at: new Date().toISOString() }]);
      if (e.response?.status === 402) toast.error('Out of AI credits — please contact admin');
    } finally {
      setSending(false);
    }
  };
  const send = () => sendWith(input.trim());

  const newSession = () => { setSessionId(null); setMessages([]); };

  if (!token) return null;

  return (
    <>
      {/* Launcher orb — animated pulse */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ai-chat-launcher fixed bottom-6 right-6 z-50 rounded-full shadow-[0_8px_30px_rgba(124,58,237,0.45)] hover:shadow-[0_12px_40px_rgba(124,58,237,0.6)] transition-all hover:scale-105"
          data-testid="ai-chat-launcher"
          title="Ask the AI Assistant"
          aria-label="Open AI assistant"
        >
          <span className="ai-chat-launcher-orb">
            <Sparkles size={24} />
          </span>
          <span className="absolute -top-1 -right-1"><CreditBadge toolKey="ai_project_assistant" variant="onDark" /></span>
        </button>
      )}

      {/* Panel — holographic glass */}
      {open && (
        <div
          className="ai-chat-panel fixed bottom-6 right-6 z-50 w-[440px] max-w-[calc(100vw-32px)] h-[640px] max-h-[calc(100vh-40px)] flex flex-col overflow-hidden"
          data-testid="ai-chat-panel"
        >
          {/* Frosted header with animated gradient stripe */}
          <div className="ai-chat-header relative px-4 pt-3.5 pb-3 flex items-start justify-between">
            <div className="flex items-start gap-2.5">
              <div className="ai-chat-avatar shrink-0">
                <Sparkles size={15} />
              </div>
              <div>
                <div className="text-[13px] font-semibold text-stone-800 leading-tight">AI Assistant</div>
                <div className="text-[10px] text-stone-500 mt-0.5 leading-tight">Knows your BOQs · cites real rows</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {unlimited ? (
                <span
                  className="ai-chat-credits-pill ai-chat-credits-unlimited"
                  data-testid="ai-chat-credits"
                  title="Unlimited AI usage for your role"
                >
                  <InfinityIcon size={12} strokeWidth={2.4} />
                </span>
              ) : credits !== null ? (
                <span
                  className={`ai-chat-credits-pill ${credits > 5 ? 'ai-chat-credits-ok' : 'ai-chat-credits-low'}`}
                  data-testid="ai-chat-credits"
                  title="Remaining AI credits"
                >
                  <Wallet size={10} /> {credits}
                </span>
              ) : null}
              <button onClick={newSession} title="New session" className="ai-chat-icon-btn" data-testid="ai-chat-new-session">
                <Plus size={13} />
              </button>
              <button onClick={() => setOpen(false)} title="Close" className="ai-chat-icon-btn" data-testid="ai-chat-close">
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="ai-chat-messages flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-chat-messages">
            {/* iter-364 — Sponsored placement (zone=ai_assistant).
                Only rendered inside the chat panel body so it does NOT
                interfere with AI reasoning; SponsorBadge is a pure UI
                component and NEVER surfaces its data into LLM prompts. */}
            <SponsorBadge zone="ai_assistant" limit={1} variant="premium-minimal" />
            {messages.length === 0 && (
              <div className="ai-chat-empty">
                <div className="ai-chat-orb-large">
                  <Sparkles size={28} />
                </div>
                {(() => {
                  const u = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
                  const role = (u.role || '').toLowerCase();
                  const roleHeading = {
                    client: `Hi ${u.name || ''}! Ask me about your projects.`,
                    vendor: `Hi ${u.name || ''}! Ask me about your work orders & payments.`,
                    labour: `Hi ${u.name || ''}! Ask me what's on your list.`,
                  }[role] || 'Hi! I can search your BOQ history.';
                  return <div className="font-semibold text-stone-800 text-sm mb-1" data-testid="ai-chat-welcome-heading">{roleHeading}</div>;
                })()}
                <div className="text-[11px] text-stone-500 mb-4">Tap a suggestion or type your own question.</div>
                {/* iter-338y: Pinned saved-searches (star icon) */}
                {pinnedSearches.length > 0 && (
                  <div className="w-full mb-3" data-testid="ai-chat-pinned-searches">
                    <div className="flex items-center gap-1 mb-1 text-[9px] uppercase tracking-wider text-amber-700 font-semibold">
                      <Star size={9} className="fill-amber-500 text-amber-500" />
                      Pinned
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {pinnedSearches.map((s, i) => (
                        <div key={s.id} className="group relative flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => sendWith(s.query)}
                            className="ai-chat-suggestion-chip !bg-amber-50 !border-amber-300 !text-amber-900"
                            data-testid={`ai-chat-pinned-${i}`}
                          >
                            {s.query}
                          </button>
                          <button
                            type="button"
                            onClick={() => togglePinSearch(s.query, true)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-amber-600 hover:text-amber-800"
                            title="Unpin"
                            data-testid={`ai-chat-unpin-${i}`}
                          >
                            <Star size={11} className="fill-current" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* iter-338y: Recent searches */}
                {recentSearches.length > 0 && (
                  <div className="w-full mb-3" data-testid="ai-chat-recent-searches">
                    <div className="flex items-center gap-1 mb-1 text-[9px] uppercase tracking-wider text-stone-500 font-semibold">
                      <Clock size={9} />
                      Recent
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {recentSearches.slice(0, 5).map((s, i) => (
                        <div key={s.id} className="group relative flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => sendWith(s.query)}
                            className="ai-chat-suggestion-chip"
                            data-testid={`ai-chat-recent-${i}`}
                          >
                            {s.query}
                          </button>
                          <button
                            type="button"
                            onClick={() => togglePinSearch(s.query, false)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400 hover:text-amber-600"
                            title="Pin"
                            data-testid={`ai-chat-pin-${i}`}
                          >
                            <Star size={11} />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteSavedSearch(s.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400 hover:text-red-500"
                            title="Remove"
                            data-testid={`ai-chat-recent-del-${i}`}
                          >
                            <X size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Role-based suggestions (always show as last row) */}
                {(pinnedSearches.length > 0 || recentSearches.length > 0) && (
                  <div className="text-[9px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Suggestions</div>
                )}
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {getRoleSuggestions().map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => sendWith(s)}
                      className="ai-chat-suggestion-chip"
                      data-testid={`ai-chat-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`ai-chat-msg-${m.role}-${i}`}
              >
                <div className={`ai-chat-bubble ${m.role === 'user' ? 'ai-chat-bubble-user' : m.role === 'system' ? 'ai-chat-bubble-system' : 'ai-chat-bubble-assistant'}`}>
                  <div className="whitespace-pre-wrap text-[12.5px] leading-relaxed">{m.content}</div>
                  {m.role === 'assistant' && m.tool_used && m.tool_results?.length > 0 && (
                    <BOQResultsList rows={m.tool_results} messageIndex={i} />
                  )}
                  {/* iter-338o: render universal tool result cards (vendor subs, payments, nav, etc.) */}
                  {m.role === 'assistant' && (m.tool_calls || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-stone-200/70 space-y-2" data-testid={`ai-chat-tool-calls-${i}`}>
                      {m.tool_calls.map((tc, k) => {
                        if (tc.tool === 'navigate') {
                          return (
                            <div key={k} className="flex flex-wrap gap-1.5" data-testid={`ai-chat-nav-${i}`}>
                              {(tc.links || []).map((lnk, l) => (
                                <a key={l} href={lnk.href} target="_blank" rel="noopener noreferrer"
                                  className="ai-chat-suggestion-chip" data-testid={`ai-chat-nav-link-${i}-${l}`}>
                                  {lnk.label} ↗
                                </a>
                              ))}
                            </div>
                          );
                        }
                        if (tc.tool === 'vendor_submissions_search') {
                          return (
                            <div key={k} className="space-y-1" data-testid={`ai-chat-vsub-${i}`}>
                              {(tc.rows || []).map((r, l) => (
                                <a key={l}
                                  href={`/vendor-submissions?focusItem=${encodeURIComponent(r.item_id || '')}&openSubmission=${encodeURIComponent(r.submission_id)}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="ai-chat-card" style={{ borderColor: 'rgba(251,191,36,0.5)', background: 'linear-gradient(135deg, rgba(254,243,199,0.7), rgba(254,249,195,0.7))' }}
                                  data-testid={`ai-chat-vsub-card-${i}-${l}`}>
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-semibold text-stone-800 truncate text-[11.5px]">{r.task_title || '—'}</div>
                                    <ExternalLink size={10} className="text-amber-700 shrink-0" />
                                  </div>
                                  <div className="text-stone-500 text-[10px] mt-0.5 truncate">
                                    Vendor: {r.vendor_name || '—'} · {r.submission_type} · {r.project_name || '—'}
                                  </div>
                                  <div className="text-stone-500 text-[10px]">
                                    {r.qty ?? '—'} {r.unit || ''} · {fmtINR(r.rate)} · {r.status}
                                  </div>
                                </a>
                              ))}
                            </div>
                          );
                        }
                        if (tc.tool === 'project_profitability') {
                          return (
                            <div key={k} className="space-y-1" data-testid={`ai-chat-prof-${i}`}>
                              {(tc.rows || []).map((r, l) => (
                                <a key={l} href={`/projects/${r.project_id}/dashboard`} target="_blank" rel="noopener noreferrer" className="ai-chat-card" data-testid={`ai-chat-prof-card-${i}-${l}`}>
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-semibold text-stone-800 truncate text-[11.5px]">{r.project_name}</div>
                                    <ExternalLink size={10} className="text-violet-600 shrink-0" />
                                  </div>
                                  <div className="text-stone-500 text-[10px] mt-0.5">
                                    Est: {r.est_margin_pct}% · Actual: <span className={r.act_margin_pct < 0 ? 'text-red-600 font-semibold' : 'text-emerald-700 font-semibold'}>{r.act_margin_pct}%</span> · Profit: {fmtINR(r.act_profit)}
                                  </div>
                                </a>
                              ))}
                            </div>
                          );
                        }
                        if (tc.tool === 'next_pending_tasks') {
                          return (
                            <div key={k} className="space-y-1" data-testid={`ai-chat-tasks-${i}`}>
                              {(tc.rows || []).map((r, l) => (
                                <a key={l} href={`/gantt?project=${r.project_id}`} target="_blank" rel="noopener noreferrer" className="ai-chat-card" data-testid={`ai-chat-task-card-${i}-${l}`}>
                                  <div className="font-semibold text-stone-800 truncate text-[11.5px]">{r.title}</div>
                                  <div className="text-stone-500 text-[10px] mt-0.5">
                                    {r.status} · {r.progress}% · Due {r.deadline || '—'}
                                  </div>
                                </a>
                              ))}
                            </div>
                          );
                        }
                        if (tc.tool === 'client_payments_summary' || tc.tool === 'vendor_payments_summary') {
                          const isVendor = tc.tool === 'vendor_payments_summary';
                          return (
                            <div key={k} className="space-y-1" data-testid={`ai-chat-pay-${i}`}>
                              {(tc.rows || []).map((r, l) => (
                                <a key={l} href={r.project_id ? (isVendor ? `/projects/${r.project_id}/dashboard` : '/payments') : '/payments'} target="_blank" rel="noopener noreferrer" className="ai-chat-card" data-testid={`ai-chat-pay-card-${i}-${l}`}>
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-semibold text-stone-800 text-[11.5px]">{fmtINR(r.amount)}</div>
                                    <span className="text-stone-500 text-[10px]">{r.payment_date}</span>
                                  </div>
                                  <div className="text-stone-500 text-[10px] mt-0.5">
                                    {isVendor ? `${r.vendor_name || '—'} · ${r.legend_category_code || '—'}` : `${r.payment_mode || '—'}`} · {r.project_name || '—'}
                                  </div>
                                </a>
                              ))}
                            </div>
                          );
                        }
                        if (tc.tool === 'generate_report') {
                          return (
                            <div key={k} className="space-y-1" data-testid={`ai-chat-reports-${i}`}>
                              {(tc.reports || []).map((rp, l) => (
                                <button
                                  key={l}
                                  type="button"
                                  onClick={() => downloadReport(rp.endpoint, rp.filename)}
                                  className="ai-chat-card w-full text-left"
                                  style={{ borderColor: 'rgba(16,185,129,0.55)', background: 'linear-gradient(135deg, rgba(220,252,231,0.7), rgba(209,250,229,0.7))' }}
                                  data-testid={`ai-chat-report-card-${i}-${l}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="font-semibold text-stone-800 truncate text-[11.5px] flex items-center gap-1.5">
                                      {rp.format === 'excel' ? <FileSpreadsheet size={11} className="text-emerald-700" /> : <FileText size={11} className="text-emerald-700" />}
                                      {rp.label}
                                    </div>
                                    <Download size={11} className="text-emerald-700 shrink-0" />
                                  </div>
                                  <div className="text-stone-500 text-[10px] mt-0.5 truncate">
                                    {rp.target} · {rp.format.toUpperCase()}
                                  </div>
                                </button>
                              ))}
                            </div>
                          );
                        }
                        if (tc.tool === 'email_report' && tc.draft) {
                          return (
                            <EmailReportCard
                              key={k}
                              draft={tc.draft}
                              testIdPrefix={`ai-chat-email-${i}`}
                            />
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                  <div className="text-[9px] mt-1.5 opacity-60">{fmt(m.created_at)}</div>
                  {/* iter-338p: semantic expansion terms — show which synonyms the AI considered */}
                  {m.role === 'assistant' && (m.semantic_terms || []).length > 1 && (
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`ai-chat-semantic-${i}`}>
                      <span className="text-[8.5px] uppercase tracking-wider text-violet-600 font-semibold">Searched also</span>
                      {(m.semantic_terms || []).slice(1, 7).map((t, k) => (
                        <span key={k} className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="ai-chat-bubble ai-chat-bubble-assistant inline-flex items-center gap-2 text-stone-500 text-xs">
                  <Loader2 size={12} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="ai-chat-composer">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={2}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  // auto-grow up to ~6 rows
                  if (inputRef.current) {
                    inputRef.current.style.height = 'auto';
                    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 140)}px`;
                  }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about BOQ items, projects, history…"
                className="ai-chat-textarea"
                disabled={sending}
                data-testid="ai-chat-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !input.trim()}
                className={`ai-chat-send ${sending || !input.trim() ? 'opacity-40 cursor-not-allowed' : ''}`}
                data-testid="ai-chat-send"
                aria-label="Send"
              >
                <Send size={15} />
              </button>
            </div>
            <div className="text-[9px] text-stone-500 mt-1.5 px-1 flex items-center gap-1">
              {unlimited ? <><InfinityIcon size={9} /> Unlimited usage</> : `1 credit per message · ${credits ?? '—'} remaining`}
              <span className="ml-auto opacity-60">⏎ to send · ⇧⏎ for new line</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
